import { ApolloError } from '@apollo/client';

import { useExpenseAggregatesQuery } from 'core/graphql';

import { IExpenseAggregateRow } from './types';
import { isPermissionDeniedError } from './errors';

interface IExpenseAggregatesHook {
  rows: IExpenseAggregateRow[];
  loading: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

interface IExpenseAggregatesOptions {
  /** Restrict the aggregate to one teacher — backs Teacher Payouts chart. */
  teacherId?: string;
  skip?: boolean;
}

/**
 * Loads pre-computed expense buckets (TZ §3.8). `groupBy` is one of
 * `'category' | 'month' | 'legal_entity'` — every row's `group` key is the
 * bucket label and `total` is the native (PLN) amount we plot. Phase 5
 * dropped the `totalByn` field — schema is currency-neutral now.
 * Phase 3 adds optional `teacherId` — supported by the backend resolver.
 */
export const useExpenseAggregates = (
  dateFrom: string,
  dateTo: string,
  groupBy: 'category' | 'month' | 'legal_entity',
  options: IExpenseAggregatesOptions = {},
): IExpenseAggregatesHook => {
  const { teacherId, skip = false } = options;
  const { data, loading, error, refetch } = useExpenseAggregatesQuery({
    variables: {
      dateFrom,
      dateTo,
      groupBy,
      teacherId: teacherId || undefined,
    },
    skip: skip || !dateFrom || !dateTo,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const rows = (
    data?.expenseAggregates || []
  ).filter((row): row is IExpenseAggregateRow => Boolean(row));

  return {
    rows,
    loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch: () => {
      refetch();
    },
  };
};
